import { useEffect, useRef, useState } from "react";

interface InlineTextEditorProps {
  value: string;
  placeholder?: string;
  onSave: (value: string) => void;
}

export function InlineTextEditor({
  value: initialValue,
  placeholder = "Click to add",
  onSave,
}: InlineTextEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync internal state when prop changes (e.g., from edit modal save)
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleSave = () => {
    if (value !== initialValue) {
      onSave(value);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      setValue(initialValue);
      setIsEditing(false);
    }
  };

  const startEditing = () => {
    setIsEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className="w-full rounded border border-blue-500 px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
    );
  }

  return (
    <span
      onClick={startEditing}
      className="block cursor-text rounded pr-3 py-1 text-sm text-gray-700 hover:bg-gray-100"
      title="Click to edit"
    >
      {value || placeholder}
    </span>
  );
}
